import React, { useEffect, useState } from "react";
import { api, inrPlain } from "@/lib/api";
import { useGroup } from "@/context/GroupContext";
import { ListSkeleton } from "@/components/Skeletons";
import { format } from "date-fns";
import { Smartphone, Coins, Landmark, CreditCard } from "lucide-react";

const ICONS = { UPI: Smartphone, Cash: Coins, Bank: Landmark, Card: CreditCard };

export default function SettlementHistory() {
  const { groupId, members } = useGroup();
  const [rows, setRows] = useState(null);
  const users = React.useMemo(() => Object.fromEntries((members || []).map((u) => [u.id, u])), [members]);

  useEffect(() => {
    if (!groupId) return;
    api.get("/settlements", { params: { group_id: groupId } }).then((s) => setRows(s.data));
  }, [groupId]);

  if (!rows) return <ListSkeleton rows={5} />;

  return (
    <div className="space-y-5 lg:max-w-4xl">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">History</div>
        <h1 className="heading text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">Settlements</h1>
      </div>

      {rows.length === 0 && (
        <div className="text-center py-10 text-slate-500 text-sm">No settlements yet.</div>
      )}

      <div className="space-y-3">
        {rows.map((s) => {
          const Icon = ICONS[s.mode] || Smartphone;
          const payer = users[s.payer_id];
          const receiver = users[s.receiver_id];
          return (
            <div key={s.id} className="p-4 rounded-2xl bg-white border border-slate-200" data-testid={`settlement-${s.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-emerald-50 grid place-items-center">
                  <Icon className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="flex-1">
                  <div className="text-sm text-slate-900 font-semibold">
                    {payer?.name} <span className="text-slate-400">paid</span> {receiver?.name}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {s.mode} • {format(new Date(s.created_at), "PP")}{s.reference_number ? ` • ${s.reference_number}` : ""}
                  </div>
                </div>
                <div className="amount font-bold text-slate-900">{inrPlain(s.amount)}</div>
              </div>
              {s.remarks && <div className="mt-3 text-xs text-slate-600 pl-14">{s.remarks}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
